import { DATADOG_ACTIONS } from '@app/app-constants'
import { toast } from '@app/components/ui/Toast/toast'
import { riskMapInstanceAtom } from '@app/risk-migration/atoms/riskMap.atoms'
import { riskAvailableWorkspacesAtom } from '@app/state/riskAtoms'
import { logDataDogAction } from '@app/utils/logger'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { appLink } from '@app/config/appLink'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'
import { useWorkspaceLoader } from './useWorkspaceLoader'
import { useLayerDataFetcher } from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDataFetcher'
export const useLoadWorkspaceFromUrl = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const workspaceId = searchParams.get('workspace')

  const map = useAtomValue(riskMapInstanceAtom)
  const layerData = useLayerDataFetcher()
  const { loadWorkspace } = useWorkspaceLoader()
  const workspaces = useAtomValue(riskAvailableWorkspacesAtom)
  const { liveLayers } = useLiveContext()
  const { pathname } = useLocation()
  const isLiveMap = pathname.startsWith(appLink.rexLive.path)

  const resolvedWorkspace = useMemo(() => {
    if (workspaceId === null) return null
    return workspaces.data?.find((workspace) => workspace.id === workspaceId) ?? null
  }, [workspaceId, workspaces.data])

  const consumedWorkspaceId = useRef<string | null>(null)

  useEffect(() => {
    if (!map || !layerData || !resolvedWorkspace) return
    if (consumedWorkspaceId.current === resolvedWorkspace.id) return
    // Live feeds load async on the live map, so wait for them before restoring live layers
    if (isLiveMap && resolvedWorkspace.layers.live?.length && !liveLayers.length) return

    consumedWorkspaceId.current = resolvedWorkspace.id
    setSearchParams(
      (params) => {
        params.delete('workspace')
        return params
      },
      { replace: true },
    )

    loadWorkspace(resolvedWorkspace)
      .then(() => {
        logDataDogAction(DATADOG_ACTIONS.WORKSPACE_LOADED, { name: resolvedWorkspace.name })
        toast({
          title: (
            <span>
              <b>{resolvedWorkspace.name}</b> is loaded
            </span>
          ),
          duration: 3000,
          type: 'success',
          position: 'top-right',
        })
      })
      .catch((error: unknown) => {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to load Workspace',
          duration: 3000,
          type: 'error',
          position: 'top-right',
        })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, layerData, resolvedWorkspace, setSearchParams, isLiveMap, liveLayers])
}
